import { createQueryParams } from '@/_utils/createQueryParams';
import { encodeURIParamsLegacy } from '@/_utils/encode-uri-params';
import { BagPaymentTermListApiService } from '@/bag/bag-payment-term-list/bag-payment-term-list-api.service';
import {
	BagPaymentOrganization,
	BagPaymentTermFilters,
	BagPaymentTermListTableColumn,
	BagPaymentTermParameters,
	BagPaymentTermTableItem,
	isBagPaymentTermTableItem,
} from '@/bag/bag-payment-term-list/bag-payment-term-list.model';
import { Data2Service } from '@/common/data2.service';
import { DownloadFileService } from '@/common/download-file.service';
import { PaymentTermKind } from '@/common/enums/PaymentTermKind';
import { PAYMENT_STATUSES_MAP, PaymentStatuses } from '@/common/enums/Status';
import { LoaderService } from '@/common/loader/loader.service';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { firstValueFrom } from 'rxjs';

@Component({
	selector: 'app-bag-payment-term-list',
	templateUrl: './bag-payment-term-list.component.html',
	styleUrls: ['./bag-payment-term-list.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BagPaymentTermListComponent implements OnInit {
	readonly data = signal<BagPaymentTermTableItem[]>([]);

	readonly parameters = signal<BagPaymentTermParameters>({
		agencies: [],
		status: [
			{ id: PaymentStatuses.PAID, name: PAYMENT_STATUSES_MAP[PaymentStatuses.PAID] },
			{ id: PaymentStatuses.NOT_PAID, name: PAYMENT_STATUSES_MAP[PaymentStatuses.NOT_PAID] },
			{ id: PaymentStatuses.PARTIALY_PAID, name: PAYMENT_STATUSES_MAP[PaymentStatuses.PARTIALY_PAID] },
			{ id: PaymentStatuses.PAID_TOO_MUCH, name: PAYMENT_STATUSES_MAP[PaymentStatuses.PAID_TOO_MUCH] },
		],
	});

	readonly columns: BagPaymentTermListTableColumn[] = [
		{ id: 'term', name: 'Termin' },
		{ id: 'value', name: 'Kwota' },
		{ id: 'vindicationAgent', name: 'Windykacja agent' },
		{ id: 'accountNumber', name: 'Konta bankowe' },
		{ id: 'status', name: 'Status' },
		{ id: 'reservationSeller', name: 'Agent' },
		{ id: 'refundRecipientName', name: 'Dane do zwrotu' },
		{ id: 'ownerName', name: 'Właściciel' },
		{ id: 'invoiceNumber', name: 'Nr faktury' },
		{ id: 'tableId', name: 'Numer rezerwacji' },
	];

	readonly page = signal(0);
	readonly pageSize = signal(50);
	readonly allItemsSize = signal(0);
	readonly summaryTotalAmount = signal(0);
	readonly summaryCommissionWithInvoice = signal(0);
	readonly summaryCommissionWithoutInvoice = signal(0);
	readonly selectedKind = signal<string | null>(null);
	readonly showCommissionSummary = computed(() => this.selectedKind() === PaymentTermKind.COMMISSION);
	readonly paymentTermCache = signal<Record<number, BagPaymentTermTableItem[]>>({});
	readonly ordering = signal<string | null>(null);

	private readonly api = inject(Data2Service);
	private readonly loader = inject(LoaderService);
	private readonly bagPaymentTermListApiService = inject(BagPaymentTermListApiService);
	private readonly downloadFileService = inject(DownloadFileService);
	private readonly destroyRef = inject(DestroyRef);

	private filters: BagPaymentTermFilters | undefined;

	ngOnInit(): void {
		this.loader.show();
		this.loadParameters();
	}

	filtersSelected(filters: BagPaymentTermFilters): void {
		this.filters = filters;
		this.selectedKind.set(filters.kind);
		this.paymentTermCache.set({});
		this.page.set(0);
		this.allItemsSize.set(0);
		this.loadData();
		this.loadSummary();
	}

	pageChanged(page: PageEvent): void {
		if (page.pageSize !== this.pageSize()) {
			this.paymentTermCache.set({});
		}
		this.page.set(page.pageIndex);
		this.pageSize.set(page.pageSize);
		this.loadData();
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}

	onSortChanged(ordering: string | null): void {
		this.ordering.set(ordering);
		this.paymentTermCache.set({});
		this.page.set(0);
		this.loadData();
	}

	exportCsv(): void {
		if (!this.filters) {
			return;
		}

		const filtersWithoutPagination: BagPaymentTermFilters = { ...this.filters };
		delete filtersWithoutPagination.limit;
		delete filtersWithoutPagination.offset;

		const params = encodeURIParamsLegacy(this.api.getChecked(filtersWithoutPagination, this.parameters() as never));

		this.bagPaymentTermListApiService
			.getPaymentTermsCsv(params)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((blob) => {
				this.downloadFileService.downloadBlob(blob, 'terminy-platnosci.csv');
			});
	}

	private loadParameters(): void {
		const agenciesQueryParams = createQueryParams({ is_oskar: false }, '9999');

		firstValueFrom(this.bagPaymentTermListApiService.getAgencies(agenciesQueryParams))
			.then((agencies) => {
				this.parameters.update((current) => ({
					...current,
					agencies: this.normalizeOrganizations(agencies),
				}));
				this.loadData();
			})
			.catch(() => {
				this.loader.hide();
			});
	}

	private loadData(): void {
		if (!this.filters) {
			return;
		}

		this.filters.limit = this.pageSize();
		this.filters.offset = this.page() * this.pageSize();
		this.filters.ordering = this.ordering();

		const cachedData = this.paymentTermCache()[this.page()];
		if (cachedData) {
			this.data.set(cachedData);
			return;
		}

		this.loader.show();
		const params = encodeURIParamsLegacy(this.api.getChecked(this.filters, this.parameters() as never));

		firstValueFrom(this.bagPaymentTermListApiService.getPaymentTerms(params))
			.then((result) => {
				const rows = result.results.filter(isBagPaymentTermTableItem);
				this.allItemsSize.set(result.count);
				this.data.set(rows);
				this.paymentTermCache.set({ ...this.paymentTermCache(), [this.page()]: rows });
				this.loader.hide();
			})
			.catch(() => {
				this.loader.hide();
			});
	}

	private loadSummary(): void {
		if (!this.filters) {
			return;
		}

		const filtersWithoutPagination: BagPaymentTermFilters = { ...this.filters };
		delete filtersWithoutPagination.limit;
		delete filtersWithoutPagination.offset;

		const params = encodeURIParamsLegacy(this.api.getChecked(filtersWithoutPagination, this.parameters() as never));

		firstValueFrom(this.bagPaymentTermListApiService.getPaymentTermsAmount(params)).then((result) => {
			this.summaryTotalAmount.set(result.totalAmount);
			this.summaryCommissionWithInvoice.set(result.commissionWithInvoice);
			this.summaryCommissionWithoutInvoice.set(result.commissionWithoutInvoice);
		});
	}

	private normalizeOrganizations(organizations: BagPaymentOrganization[]): BagPaymentOrganization[] {
		return organizations.map((organization) => ({
			...organization,
			id: organization.id.toString(),
		}));
	}
}
